import React from 'react';
import { view, i18next, utils } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import controlIds from '../../constants/control-ids';
import appearanceAttributes from '../../constants/appearance-attributes';
import controlUnits from '../../constants/control-units';

const { Paragraph, Icon } = designSystem;

const SUPPORTED_UNITS = [controlUnits.PERCENT];

const PX_RANGE = { min: 0, max: 128, step: 1 };
const PERCENT_RANGE = { min: 0, max: 100, step: 1 };

const getRange = (value) => (`${value}`.endsWith(controlUnits.PERCENT) ? PERCENT_RANGE : PX_RANGE);
const getMin = (value) => getRange(value).min;
const getMax = (value) => getRange(value).max;
const getStep = (value) => getRange(value).step;

const BORDER_RADIUS_FALLBACK_VALUE = '4';

const DEFAULT_BORDER_TOP_LEFT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-top-left-radius';
const DEFAULT_BORDER_TOP_RIGHT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-top-right-radius';
const DEFAULT_BORDER_BOTTOM_LEFT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-bottom-left-radius';
const DEFAULT_BORDER_BOTTOM_RIGHT_RADIUS_CUSTOM_PROPERTY =
  '--ws-media-container-border-bottom-right-radius';

const ATTRIBUTE_TO_CUSTOM_PROPERTY = {
  [appearanceAttributes.BORDER_TOP_LEFT_RADIUS]: DEFAULT_BORDER_TOP_LEFT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_TOP_RIGHT_RADIUS]: DEFAULT_BORDER_TOP_RIGHT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_BOTTOM_LEFT_RADIUS]:
    DEFAULT_BORDER_BOTTOM_LEFT_RADIUS_CUSTOM_PROPERTY,
  [appearanceAttributes.BORDER_BOTTOM_RIGHT_RADIUS]:
    DEFAULT_BORDER_BOTTOM_RIGHT_RADIUS_CUSTOM_PROPERTY,
};

const CORNERS = {
  topLeft: {
    id: 'top-left-corner',
    targets: [appearanceAttributes.BORDER_TOP_LEFT_RADIUS],
    iconGlyph: 'corner-top-left-radius',
  },
  topRight: {
    id: 'top-right-corner',
    targets: [appearanceAttributes.BORDER_TOP_RIGHT_RADIUS],
    iconGlyph: 'corner-top-right-radius',
  },
  bottomLeft: {
    id: 'bottom-left-corner',
    targets: [appearanceAttributes.BORDER_BOTTOM_LEFT_RADIUS],
    iconGlyph: 'corner-bottom-left-radius',
  },
  bottomRight: {
    id: 'bottom-right-corner',
    targets: [appearanceAttributes.BORDER_BOTTOM_RIGHT_RADIUS],
    iconGlyph: 'corner-bottom-right-radius',
  },
};

const computeCornersFallback = ({ elementId, target }) => {
  const element = view.accessors.getLiveElement(elementId);

  if (!element) return BORDER_RADIUS_FALLBACK_VALUE;

  const defaultValue = getComputedStyle(element)
    .getPropertyValue(ATTRIBUTE_TO_CUSTOM_PROPERTY[target])
    .trim();

  if (!defaultValue) return BORDER_RADIUS_FALLBACK_VALUE;

  if (defaultValue.endsWith(controlUnits.PERCENT)) {
    return defaultValue;
  }

  if (!defaultValue.endsWith(controlUnits.PX)) return BORDER_RADIUS_FALLBACK_VALUE;

  const parsedValue = `${parseFloat(defaultValue)}`;

  return utils.isNumeric(parsedValue) ? parsedValue : BORDER_RADIUS_FALLBACK_VALUE;
};

const getPopoverProps = ({ imgSrc, iconGlyph }) => ({
  imgSrc,
  renderContent: () => (
    <Paragraph appearance="white" size="small">
      {i18next.t('Adjust corners for iframe')}. {i18next.t('Click the {icon}', { icon: '' })}{' '}
      <Icon type="white" size={16} strokeWidth={2} glyph={iconGlyph} insideText />
      {i18next.t('{click_the_icon} to adjust each one separately', { click_the_icon: '' })}
    </Paragraph>
  ),
});

const getCorners = (settingId, settings = {}) => ({
  ...settings,
  id: controlIds.CORNERS,
  title: i18next.t('Independent corners'),
  targets: Object.values(CORNERS).flatMap(({ targets: itemTargets }) => itemTargets),
  computeFallbackValue: computeCornersFallback,
  iconGlyph: 'corner-radius',
  mixedLabel: i18next.t('Mixed'),
  step: getStep,
  min: getMin,
  max: getMax,
  supportedUnits: SUPPORTED_UNITS,
  items: Object.values(CORNERS).map((item) => ({
    ...item,
    computeFallbackValue: computeCornersFallback,
  })),
  popover: getPopoverProps({
    iconGlyph: 'corner-radius',
  }),
});

export default getCorners;
